import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import TopBar from './components/TopBar';
import QuickAddGrid from './components/QuickAddGrid';
import Composer from './components/Composer';
import SummaryList from './components/SummaryList';
import { useLocalStorage } from './hooks/useLocalStorage';
import { drinkPresets } from './data/presets';
import './App.css';

const { FiCoffee } = FiIcons;

function App() {
  const [orders, setOrders] = useLocalStorage('kopitiam-orders', {});
  const [sessionStart, setSessionStart] = useLocalStorage('session-start', Date.now());
  const [expandedFamilies, setExpandedFamilies] = useLocalStorage('expanded-families', ['kopi', 'teh']);
  const [composerVisible, setComposerVisible] = useState(false);

  useEffect(() => {
    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js');
    }
  }, []);

  const addOrder = (key, quantity = 1) => {
    setOrders(prev => {
      const newQuantity = (prev[key] || 0) + quantity;
      if (newQuantity <= 0) {
        const { [key]: removed, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [key]: newQuantity
      };
    });
  };

  const updateOrder = (key, quantity) => {
    if (quantity <= 0) {
      setOrders(prev => {
        const newOrders = { ...prev };
        delete newOrders[key];
        return newOrders;
      });
    } else {
      setOrders(prev => ({
        ...prev,
        [key]: quantity
      }));
    }
  };

  const resetSession = () => {
    setOrders({});
    setSessionStart(Date.now());
  };

  const toggleFamily = (family) => {
    setExpandedFamilies(prev => 
      prev.includes(family) 
        ? prev.filter(f => f !== family)
        : [...prev, family]
    );
  };

  const toggleComposer = () => {
    setComposerVisible(prev => !prev);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-amber-100 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-amber-200/30 to-orange-300/30 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-orange-200/30 to-red-300/30 rounded-full blur-3xl"
          animate={{ 
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0]
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        />
      </div>

      <TopBar sessionStart={sessionStart} onReset={resetSession} />
      
      <main className="relative z-10 px-4 pb-8 space-y-6 pt-6">
        <QuickAddGrid 
          presets={drinkPresets} 
          onAdd={addOrder} 
          orders={orders} 
          expandedFamilies={expandedFamilies}
          onToggleFamily={toggleFamily}
        />
        
        <Composer 
          onAdd={addOrder} 
          isVisible={composerVisible}
          onToggle={toggleComposer}
        />
        
        <AnimatePresence>
          {Object.keys(orders).length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.3 }}
            >
              <SummaryList orders={orders} onUpdate={updateOrder} />
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}

export default App;